import { Injectable } from "@angular/core";
import { BlobService } from "./blob.service";
import { N2PdfViewerModel } from "./pdf-viewer.model";
import { AppService } from "@app/app.service";
import { IRequestParameter, RequestParameter } from "@shared/helpers/response";
import { ToastrService } from "ngx-toastr";
import { showToastr } from "@shared/common";
import { FilterOperator } from "@shared/helpers/enums";
import { ConfigService } from "@app/config.service";

@Injectable({
  providedIn: "root",
})
export class PdfService {
  private readonly _blobService = BlobService();
  private _responseTypeBlob = {
    responseType: "blob" as "json",
  };

  url: string;
  displayViewer = false;
  disableXlsx = true;
  params: IRequestParameter;
  fileName = "Reporte";
  onHideReport: Function = () => {};

  constructor(
    private configService: ConfigService,
    private appService: AppService,
    private toastr: ToastrService
  ) {
    this.url = this.configService.getUrlApi("api/");
  }

  private internalCallback(
    response: Blob,
    fileName: string,
    callback: Function,
    onHideAction: Function = () => {},
    generateXls = false
  ) {
    this.disableXlsx = true;
    if (callback) callback();

    if (!response) return;

    this._blobService.subject.next(
      new N2PdfViewerModel(fileName, response, generateXls)
    );
    this.appService.blockUI = true;
    this.onHideReport = onHideAction;
  }

  resetParams() {
    this.params = new RequestParameter();
    this.fileName = "Reporte";
  }

  /**
   * He agregado el parametero de participanteId para poder cumplir con el requerimiento de poder
   * visualizar multiples constancias de inscripción. Aunque parametrizamos el backend para soportar
   * varias inscripciones de sorteos existia la restriccion en la db PK(DocumentoId,Rnc) lo que dificultaba
   * poder generar una constancia a cada sorteo.
   * Ver los archivos afectados en el commit: Agregar funcionalidad para generar constancias de inscripción por sorteo
   * Modificado por: alcaab 30/12/2020
   *
   * @memberof PdfService
   */
  getConstanciaInscripcion(participanteId: number, callback?: Function) {
    this.configService.http
      .get(
        `${this.url}Participante/GetConstanciaInscripcionBlobFile/${participanteId}`,
        this._responseTypeBlob
      )
      .subscribe((response: Blob) =>
        this.internalCallback(response, "Formulario de Inscripción", callback)
      );
  }

  getDocumentoBlob({ DocumentoId, Descripcion }, callback?: Function) {
    this.configService.http
      .get(
        `${this.url}ParticipanteDocumento/GetBlobFile/${DocumentoId}`,
        this._responseTypeBlob
      )
      .subscribe((response: Blob) =>
        this.internalCallback(response, Descripcion, callback)
      );
  }

  getRecepcionDocumentoPdf(
    id: number,
    descripcion: string,
    trigger: string,
    callback?: Function
  ) {
    this.configService.http
      .get(
        `${this.url}Recepcion/GetRecepcionDocumentoPdf/${id}/${trigger}`,
        this._responseTypeBlob
      )
      .subscribe((response: Blob) =>
        this.internalCallback(response, descripcion, callback)
      );
  }

  getContanciaRecepcionPdf(
    id: number,
    callback: Function,
    onHideAction: Function
  ) {
    this.configService.http
      .get(
        `${this.url}Recepcion/GetConstanciaRecepcionPdf/${id}`,
        this._responseTypeBlob
      )
      .subscribe((response: Blob) =>
        this.internalCallback(
          response,
          "Formulario de Recepción de Documentos",
          callback,
          onHideAction
        )
      );
  }

  generarConstanciaInscripcion(participanteId: number, callback?: Function) {
    this.configService.http
      .get(
        `${this.url}Participante/GenerarConstanciaInscripcion/${participanteId}`,
        this._responseTypeBlob
      )
      .subscribe((response: Blob) =>
        this.internalCallback(response, "Formulario de Inscripción", callback)
      );
  }

  getReporteDirectores() {
    this.configService.http
      .post(
        `${this.url}Reportes/GetReporteDirectorBlob`,
        this.params,
        this._responseTypeBlob
      )
      .subscribe((response: Blob) =>
        this.internalCallback(
          response,
          this.fileName,
          () => (this.disableXlsx = false)
        )
      );
  }

  requestBlobXlsx() {
    if (this.disableXlsx) {
      showToastr(
        this.toastr,
        "No se puede descargar este reporte en formato de excel",
        "error"
      );
      return;
    }

    this.params.filters.push({
      propertyName: "Xlsx",
      operator: FilterOperator.Equals,
      value: true,
    });

    this.configService.http
      .post(
        `${this.url}Reportes/GetReporteDirectorBlob`,
        this.params,
        this._responseTypeBlob
      )
      .subscribe((response: Blob) =>
        this.internalCallback(
          response,
          this.fileName,
          () => (this.disableXlsx = false),
          () => this.resetParams(),
          true
        )
      );
  }
}
